import React, { useEffect, useRef, useState } from 'react'
import Select, { SingleValue } from 'react-select'
import { buscarAtletas } from '../services/atletaService'

/** Dados de um atleta para seleção de parceiro */
export interface Partner {
  id: string
  nome: string
  usuario: string
  imagemUrl?: string | null
}

/** Propriedades do componente SelectPartnerModal */
export interface SelectPartnerModalProps {
  /** Indica se o modal está visível */
  open: boolean
  /** Função chamada ao fechar o modal */
  onClose: () => void
  /** Lista de parceiros exibidos na sessão "recentes" */
  recentPartners: Partner[]
  /** Callback ao selecionar um parceiro */
  onSelect: (partner: Partner) => void
}

interface OptionType {
  value: string
  label: string
  partner: Partner
}

const SelectPartnerModal: React.FC<SelectPartnerModalProps> = ({
  open,
  onClose,
  recentPartners,
  onSelect,
}) => {
  const [input, setInput] = useState('')
  const [options, setOptions] = useState<OptionType[]>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<OptionType | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const selectRef = useRef<any>(null)

  const userStr = localStorage.getItem('user')
  const loggedUserId = userStr ? (JSON.parse(userStr).id ?? '0') : '0'

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.classList.add('modal-open')
      // focus select input when opening
      setTimeout(() => selectRef.current?.inputRef?.focus(), 0)
      modalRef.current?.focus()
      setInput('')
      setSelected(null)
      setOptions([])
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.classList.remove('modal-open')
    }
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    if (!input) {
      setOptions([])
      return
    }
    const timer = setTimeout(async () => {
      try {
        setLoading(true)
        const results = await buscarAtletas(input)
        setOptions(
          results.filter(p => p.usuario?.id !== loggedUserId) // Exclude the current user
          .map(p => {
            const identificador = p.usuario?.username || p.usuario?.email || '';
            const nome = p.usuario?.nome ?? 'Atleta';
            return {
              value: p.id,
              label: identificador ? `${nome} (@${identificador})` : nome,
              partner: { id: p.id, nome, usuario: identificador, imagemUrl: p.usuario?.imagemUrl },
            };
          })
        )
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [input, open])

  if (!open) return null

  const recentOptions: OptionType[] = recentPartners.map((p) => ({
    value: p.id,
    label: `${p.nome} (@${p.usuario})`,
    partner: p,
  }))

  const handleChange = (option: SingleValue<OptionType>) => {
    if (option) {
      setSelected(option)
    } else {
      setSelected(null)
    }
  }

  const handleConfirm = () => {
    if (selected) {
      onSelect(selected.partner)
    }
    onClose()
  }

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show d-block" role="dialog" tabIndex={-1} ref={modalRef}>
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title w-100 text-center">Selecione seu parceiro</h5>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <Select
                ref={selectRef}
                options={options}
                placeholder="Procure por nome ou usuário"
                inputValue={input}
                onInputChange={(v) => setInput(v)}
                onChange={handleChange}
                menuPlacement="auto"
                classNamePrefix="react-select"
                filterOption={null}
                isLoading={loading}
                value={selected}
                noOptionsMessage={() =>
                  loading ? 'Buscando...' : 'Nenhum atleta encontrado'
                }
              />
              <h6 className="mt-4">Parceiros recentes</h6>
              {recentPartners.map((p) => (
                <div key={p.id} className="d-flex align-items-center my-2">
                  <img
                    src={p.imagemUrl || '/assets/images/user.png'}
                    alt={p.nome}
                    className="rounded-circle me-3"
                    style={{ width: 40, height: 40, objectFit: 'cover' }}
                  />
                  <div className="flex-grow-1">
                    <div className="fw-bold">{p.nome}</div>
                    <div className="text-primary">@{p.usuario}</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary ms-auto"
                    onClick={() => {
                      onSelect(p)
                      onClose()
                    }}
                  >
                    Selecionar
                  </button>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirm}
                disabled={!selected}
              >
                Selecionar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SelectPartnerModal
